import { createContext, useContext, useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { useSavedList } from './useSavedList';

const CartContext = createContext({});

export const useCart = () => useContext(CartContext);

export const CartProvider = ({ children }) => {
  const [cartItems, setCartItems, { removeSavedItems, restoreSavedItems }] = useSavedList('cart', 'cart');
  const [cartFlights, setCartFlights] = useState([]);

  const addToCart = (product, quantity = 1) => {
    restoreSavedItems([product.id]);
    setCartItems(prev => {
      const existing = prev.find(item => item.id === product.id);
      if (existing) {
        return prev.map(item => 
          item.id === product.id 
            ? {...item, quantity: item.quantity + quantity}
            : item
        );
      }
      return [...prev, {...product, quantity }];
    });
  };

  const addToCartAnimated = (product, sourceElement) => {
    addToCart(product);

    const cart = document.querySelector('[data-cart-target]')?.getBoundingClientRect();
    let sourceImage = sourceElement;
    while (sourceImage && sourceImage.tagName !== 'IMG') {
      sourceImage = sourceImage.parentElement?.querySelector('img') || sourceImage.parentElement;
    }
    const source = sourceImage?.getBoundingClientRect();
    if (!source || !cart || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const flightSize = Math.min(source.width * 0.25, 92);
    const left = source.left + (source.width - flightSize) / 2;
    const top = source.top + (source.height - flightSize) / 2;
    setCartFlights((current) => [...current, {
      id: `${product.id}-${Date.now()}-${Math.random()}`,
      image: product.image,
      left,
      top,
      width: flightSize,
      height: flightSize,
      x: cart.left + cart.width / 2 - (left + flightSize / 2),
      y: cart.top + cart.height / 2 - (top + flightSize / 2),
    }]);
  };

  const removeCartFlight = (id) => setCartFlights((current) => current.filter((flight) => flight.id !== id));

  const removeFromCart = (id) => removeSavedItems([id]);

  const updateQuantity = (id, quantity) => {
    if (quantity === 0) {
      removeFromCart(id);
      return;
    }
    setCartItems(prev => prev.map(item => 
      item.id === id ? {...item, quantity} : item
    ));
  };

  const clearCart = () => removeSavedItems(cartItems.map((item) => item.id));

  const totalItems = cartItems.reduce((sum, item) => sum + item.quantity, 0);
  const totalPrice = cartItems.reduce((sum, item) => sum + (item.price * item.quantity), 0);

  const value = {
    cartItems,
    addToCart,
    addToCartAnimated,
    cartFlights,
    removeCartFlight,
    removeFromCart,
    updateQuantity,
    clearCart,
    totalItems,
    totalPrice,
  };

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
};

export const CartFlightLayer = () => {
  const { cartFlights, removeCartFlight } = useCart();
  const prefersReducedMotion = useReducedMotion();

  return (
    <AnimatePresence>
      {cartFlights.map((flight) => (
        <motion.div
          key={flight.id}
          initial={{ x: 0, y: 0, scale: 1, rotate: 0, opacity: 1 }}
          animate={{
            x: [0, flight.x * 0.34, flight.x],
            y: [0, flight.y * 0.6 - 12, flight.y + 8],
            scale: [1, 0.68, 0.08],
            rotate: [0, -7, 15],
            opacity: [1, 1, 0],
          }}
          transition={{ duration: prefersReducedMotion ? 0.12 : 0.88, times: [0, 0.62, 1], ease: ['easeOut', 'easeIn'] }}
          onAnimationComplete={() => removeCartFlight(flight.id)}
          aria-hidden="true"
          className="pointer-events-none fixed z-[100] overflow-hidden rounded-sm shadow-[0_16px_42px_rgba(0,0,0,0.34)]"
          style={{ left: flight.left, top: flight.top, width: flight.width, height: flight.height }}
        >
          <img src={flight.image} alt="" className="h-full w-full object-contain" />
        </motion.div>
      ))}
    </AnimatePresence>
  );
};

